import { linkGoogleApi, loginGoogleApi, registerGoogleApi } from "@/api/services/main/auth";
import { CODE_QUERY_KEY, STATE_QUERY_KEY } from "@/features/auth/constant/login";
import { AuthLoadingScreen } from "@/features/auth/components/AuthLoadingScreen";
import { usePostLoginRedirect } from "@/features/auth/hooks/usePostLoginRedirect";
import { decodeCognitoAuthState } from "@/features/auth/utils/cognitoAuthState";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { createUrlWithParams } from "@/utils/url";
import { FC, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { logout } from "@/utils/logout";

export const AuthCallbackCognitoPage: FC = () => {
  const navigate = useNavigate();
  const redirectAfterLogin = usePostLoginRedirect();
  const [searchParams] = useSearchParams();
  const isProcessing = useRef(false);

  const code = searchParams.get(CODE_QUERY_KEY);

  const rawState = searchParams.get(STATE_QUERY_KEY);
  const { externalId, redirectUrl, productCode, email, registerResetToken, linkGoogle } = useMemo(() => decodeCognitoAuthState(rawState), [rawState]);

  useEffect(() => {
    // The link flow is authenticated and only needs `code`; other flows also require `externalId`.
    if (!code || (!externalId && !linkGoogle)) {
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    // Prevent double execution in StrictMode
    if (isProcessing.current) return;
    isProcessing.current = true;

    const exchangeCode = async () => {
      try {
        // Redirect started from the user-setting "link Google" flow: the user is already signed in.
        if (linkGoogle) {
          await linkGoogleApi({ code });
          toast.success("Googleログインと接続しました。");
          navigate(AppRoutes.userSettingDetail, { replace: true });
          return;
        }

        // Presence of a reset token means this redirect came from the register form.
        if (registerResetToken) {
          await registerGoogleApi({ code, externalId: externalId!, token: registerResetToken });
          navigate(AppRoutes.registerComplete, { replace: true, state: { email, externalId } });
          return;
        }

        await logout();
        await loginGoogleApi({ code, externalId: externalId! });
        // OAuth (SSO) logins mint a product token first; standard logins go to the safe target.
        redirectAfterLogin({ productCode, redirectUrl }, { replace: true });
      } catch (error) {
        showError(error);
        if (linkGoogle) {
          navigate(AppRoutes.userSettingDetail, { replace: true });
          return;
        }
        if (registerResetToken) {
          navigate(createUrlWithParams(AppRoutes.register, { email, resetToken: registerResetToken, externalId }), {
            replace: true,
          });
          return;
        }
        navigate(createUrlWithParams(AppRoutes.loginCredentials, { email, externalId }), { replace: true });
      }
    };

    exchangeCode();
  }, [code, externalId, redirectUrl, registerResetToken, email, productCode, linkGoogle]);

  return <AuthLoadingScreen />;
};
